import type { MockOrder } from './types';

/**
 * Five hand-curated rows covering the chain-pricing scenario matrix.
 * The order matters: it's also the visual "demo script" we walk the
 * user through:
 *
 *   1. internal own-fleet, NO contracts yet (price card empty state)
 *   2. direct shipper -> carrier, 1 leg, agreement-based
 *   3. on-behalf-of CRM client, 1 leg, custom price
 *   4. broker chain (2 legs, broker margin row visible)
 *   5. multi-broker chain (3 legs)
 */
const HOUR = 60;
const DAY = HOUR * 24;

export const mockOrders: MockOrder[] = [
  {
    id: 'mock-001',
    ref: 'HR-10001',
    createdAt: new Date(Date.now() - 2 * HOUR * 60_000),
    status: 'searching',
    shipper: { name: 'Almutairi Logistics', role: 'carrier' },
    client: undefined,
    carrier: { name: 'Almutairi Logistics', type: 'own_fleet' },
    driver: undefined,
    legs: [],
    load: { vehicleType: 'Reefer (10t)', weightKg: 7400, tempC: 4 },
    pickup: { city: 'Riyadh', date: new Date(Date.now() + 6 * HOUR * 60_000) },
    dropoff: { city: 'Dammam' },
    pricingMode: 'own_fleet',
  },
  {
    id: 'mock-002',
    ref: 'HR-10002',
    createdAt: new Date(Date.now() - 1 * DAY * 60_000),
    status: 'assigned',
    shipper: { name: 'Tamer Group', role: 'business' },
    client: { name: 'Tamer Group', isInternal: true },
    carrier: { name: 'SwiftFleet', type: 'partner' },
    driver: {
      name: 'Faisal A.',
      phone: '+966 50 123 4567',
      lastSeenMinutesAgo: 4,
    },
    legs: [
      {
        buyer: 'Tamer Group',
        seller: 'SwiftFleet',
        amount: 1850,
        currency: 'SAR',
        pricingSource: 'agreement',
      },
    ],
    load: { vehicleType: 'Box truck (5t)', weightKg: 3200 },
    pickup: { city: 'Jeddah', date: new Date(Date.now() + 1 * DAY * 60_000) },
    dropoff: { city: 'Mecca' },
    pricingMode: 'private_network',
  },
  {
    id: 'mock-003',
    ref: 'HR-10003',
    createdAt: new Date(Date.now() - 3 * HOUR * 60_000),
    status: 'in_transit',
    shipper: { name: 'BlueSky Carrier', role: 'carrier' },
    client: { name: 'NadecFoods', isInternal: false },
    carrier: { name: 'BlueSky Carrier', type: 'own_fleet' },
    driver: {
      name: 'Saeed K.',
      phone: '+966 55 222 9988',
      lastSeenMinutesAgo: 12,
    },
    legs: [
      {
        buyer: 'NadecFoods',
        seller: 'BlueSky Carrier',
        amount: 2400,
        currency: 'SAR',
        pricingSource: 'custom',
      },
    ],
    load: { vehicleType: 'Reefer (15t)', weightKg: 11_500, tempC: -18 },
    pickup: { city: 'Riyadh', date: new Date(Date.now() - 1 * HOUR * 60_000) },
    dropoff: { city: 'Tabuk' },
    pricingMode: 'auto',
  },
  {
    id: 'mock-004',
    ref: 'HR-10004',
    createdAt: new Date(Date.now() - 5 * HOUR * 60_000),
    status: 'assigned',
    shipper: { name: 'Hadi Trade Co.', role: 'business' },
    client: { name: 'Hadi Trade Co.', isInternal: true },
    carrier: { name: 'Northern Roads', type: 'partner' },
    driver: undefined,
    legs: [
      {
        buyer: 'Hadi Trade Co.',
        seller: 'CapacityBridge',
        amount: 4200,
        currency: 'SAR',
        pricingSource: 'agreement',
      },
      {
        buyer: 'CapacityBridge',
        seller: 'Northern Roads',
        amount: 3650,
        currency: 'SAR',
        pricingSource: 'custom',
      },
    ],
    load: { vehicleType: 'Flatbed (20t)', weightKg: 18_000 },
    pickup: { city: 'Dammam', date: new Date(Date.now() + 2 * DAY * 60_000) },
    dropoff: { city: 'Hail' },
    pricingMode: 'private_network',
  },
  {
    id: 'mock-005',
    ref: 'HR-10005',
    createdAt: new Date(Date.now() - 6 * DAY * 60_000),
    status: 'delivered',
    shipper: { name: 'Riyadh Distributors', role: 'business' },
    client: { name: 'Saudia Cargo', isInternal: false },
    carrier: { name: 'CityHaul', type: 'public' },
    driver: {
      name: 'Mohammed N.',
      phone: '+966 53 444 1212',
      lastSeenMinutesAgo: 240,
    },
    legs: [
      {
        buyer: 'Saudia Cargo',
        seller: 'BrokerOne',
        amount: 6800,
        currency: 'SAR',
        pricingSource: 'agreement',
      },
      {
        buyer: 'BrokerOne',
        seller: 'BrokerTwo',
        amount: 6200,
        currency: 'SAR',
        pricingSource: 'custom',
      },
      {
        buyer: 'BrokerTwo',
        seller: 'CityHaul',
        amount: 5450,
        currency: 'SAR',
        pricingSource: 'custom',
      },
    ],
    load: { vehicleType: 'Curtain (10t)', weightKg: 8200, packing: 'Pallets' },
    pickup: { city: 'Riyadh', date: new Date(Date.now() - 7 * DAY * 60_000) },
    dropoff: { city: 'Jeddah' },
    pricingMode: 'public_load_board',
  },
];
